<div class="mfa-enrollment-wizard">
	<h2 mat-dialog-title>Set Up Multi-Factor Authentication</h2>

	<mat-dialog-content>
		<!-- Loading -->
		<div *ngIf="currentStep === 'loading'" class="wizard-step loading-step">
			<mat-spinner diameter="40"></mat-spinner>
			<p>Preparing enrollment...</p>
		</div>

		<!-- Method Selection -->
		<div *ngIf="currentStep === 'select-method'" class="wizard-step">
			<p class="step-description">Choose how you'd like to verify your identity when signing in.</p>

			<div class="method-options">
				<button mat-raised-button
						color="primary"
						*ngIf="availableTotp"
						class="method-option"
						(click)="selectMethod('totp')">
					<mat-icon>phone_android</mat-icon>
					<div class="method-details">
						<span class="method-title">Authenticator App</span>
						<span class="method-desc">Use an app like Google Authenticator or Microsoft Authenticator</span>
					</div>
				</button>

				<button mat-raised-button
						color="primary"
						*ngIf="availableEmail"
						class="method-option"
						(click)="selectMethod('email')">
					<mat-icon>email</mat-icon>
					<div class="method-details">
						<span class="method-title">Email OTP</span>
						<span class="method-desc">Receive a code via email each time you sign in</span>
					</div>
				</button>
			</div>
		</div>

		<!-- TOTP Setup: QR Code -->
		<div *ngIf="currentStep === 'totp-setup'" class="wizard-step">
			<p class="step-description">Scan this QR code with your authenticator app, then enter the 6-digit code below.</p>

			<div class="qr-container" *ngIf="qrCodeDataUrl">
				<img [src]="qrCodeDataUrl" alt="QR Code" class="qr-image" />
			</div>

			<div class="manual-key" *ngIf="totpSetup?.manualEntryKey">
				<p class="manual-key-label">Can't scan? Enter this key manually:</p>
				<code class="manual-key-value">{{ totpSetup.manualEntryKey }}</code>
			</div>

			<mat-form-field appearance="outline" class="code-field">
				<mat-label>6-digit code</mat-label>
				<input matInput
					   [(ngModel)]="totpCode"
					   maxlength="6"
					   pattern="[0-9]*"
					   inputmode="numeric"
					   autocomplete="one-time-code"
					   (keyup.enter)="confirmTotp()" />
			</mat-form-field>
		</div>

		<!-- Email: Enter Address -->
		<div *ngIf="currentStep === 'email-send'" class="wizard-step">
			<p class="step-description">We'll send a verification code to your email address.</p>

			<mat-form-field appearance="outline" class="email-field">
				<mat-label>Email address</mat-label>
				<input matInput
					   type="email"
					   [(ngModel)]="emailAddress"
					   [readonly]="enrollmentState?.emailVerified"
					   autocomplete="email"
					   (keyup.enter)="sendEmailOtp()" />
			</mat-form-field>
		</div>

		<!-- Email: Enter Code -->
		<div *ngIf="currentStep === 'email-verify'" class="wizard-step">
			<p class="step-description">Enter the 6-digit code sent to <strong>{{ emailAddress }}</strong>.</p>

			<mat-form-field appearance="outline" class="code-field">
				<mat-label>6-digit code</mat-label>
				<input matInput
					   [(ngModel)]="emailCode"
					   maxlength="6"
					   pattern="[0-9]*"
					   inputmode="numeric"
					   autocomplete="one-time-code"
					   (keyup.enter)="confirmEmail()" />
			</mat-form-field>

			<button mat-button
					class="resend-link"
					[disabled]="!canResend || isSubmitting"
					(click)="resendEmailOtp()">
				{{ resendCountdown > 0 ? 'Resend code (' + resendCountdown + 's)' : 'Resend code' }}
			</button>
		</div>

		<!-- Complete -->
		<div *ngIf="currentStep === 'complete'" class="wizard-step complete-step">
			<mat-icon class="success-icon">check_circle</mat-icon>
			<h3>Multi-factor authentication is now active</h3>
			<p>Your account is now protected with {{ enrolledMethod === 'AuthenticatorApp' ? 'an authenticator app' : 'email OTP' }}.</p>
		</div>

		<!-- MFA Disabled Mid-Enrollment -->
		<div *ngIf="currentStep === 'mfa-disabled'" class="wizard-step complete-step">
			<mat-icon class="success-icon" style="color: #d32f2f;">error_outline</mat-icon>
			<h3>MFA Has Been Disabled</h3>
			<p>{{ mfaDisabledMessage }}</p>
		</div>

		<!-- No Methods Available -->
		<div *ngIf="currentStep === 'error'" class="wizard-step complete-step">
			<mat-icon class="success-icon" style="color: #d32f2f;">error_outline</mat-icon>
			<h3>MFA Unavailable</h3>
			<p>No MFA methods are currently available. Please contact your administrator.</p>
		</div>

		<!-- Error Message -->
		<div *ngIf="errorMessage" class="error-message">
			<mat-icon>error_outline</mat-icon>
			<span>{{ errorMessage }}</span>
		</div>

		<!-- Contact Admin — shown only where the user is stuck (login path, no Skip available). A user who
			 cannot enroll otherwise has no route to help. Mirrors the challenge screen's block. -->
		<div class="contact-info" *ngIf="showContactAdmin">
			<p class="contact-label">Need help? Contact your administrator:</p>
			<div class="contact-item" *ngIf="contactAdminPhone">
				<mat-icon>phone</mat-icon>
				<span>{{ contactAdminPhone }}</span>
			</div>
			<div class="contact-item" *ngIf="contactAdminEmail">
				<mat-icon>email</mat-icon>
				<a [href]="'mailto:' + contactAdminEmail">{{ contactAdminEmail }}</a>
			</div>
		</div>
	</mat-dialog-content>

	<!--
		 Every action in this bar is mat-stroked-button, not mat-button. Flat mat-button renders as bare text
		 in Material 14, so Back / Skip for now / Cancel / Sign out did not read as buttons at all. Stroked is
		 the SECONDARY level of the hierarchy FIX-04 set on the challenge dialog - raised primary > stroked
		 secondary > text tertiary - and it supplies the outline, focus ring, ripple and disabled state from
		 the theme, so nothing is re-implemented here. The step's per-action primary below stays raised, and
		 the inline "Resend code" link inside the content stays text tertiary, matching the challenge dialog.
	-->
	<mat-dialog-actions align="end">
		<!-- Back button -->
		<button mat-stroked-button
				*ngIf="canGoBack"
				[disabled]="isSubmitting"
				(click)="goBack()">
			Back
		</button>

		<!-- Skip button — login enrollment only. In addMode the user is already enrolled and is
			 voluntarily adding a second factor, so there is nothing to defer: they get Cancel instead. -->
		<span *ngIf="canSkipEnrollment && isEnforcedPolicy && gracePeriodDaysRemaining != null"
			  class="grace-period-text">
			{{ gracePeriodDaysRemaining >= 1
				? gracePeriodDaysRemaining + ' day' + (gracePeriodDaysRemaining !== 1 ? 's' : '') + ' remaining to complete enrollment.'
				: 'Less than 1 day remaining to complete enrollment.' }}
		</span>
		<button mat-stroked-button
				*ngIf="canSkipEnrollment"
				[disabled]="isSubmitting"
				(click)="skipEnrollment()">
			Skip for now
		</button>

		<!-- Cancel — addMode equivalent of Skip: dismisses the voluntary add without touching MFA state. -->
		<button mat-stroked-button
				*ngIf="canCancelAddMode"
				[disabled]="isSubmitting"
				(click)="cancelAddMode()">
			Cancel
		</button>

		<!-- Sign out — login path only. The one control that is always available, so an expired-grace user
			 (no Skip, by design) can still leave. It grants no portal access, so EC4 is unaffected. -->
		<button mat-stroked-button
				*ngIf="canSignOut"
				[disabled]="isSubmitting"
				(click)="signOut()">
			Sign out
		</button>

		<!-- Action buttons per step -->
		<button mat-raised-button color="primary"
				*ngIf="currentStep === 'totp-setup'"
				[disabled]="isSubmitting || !totpCode || totpCode.length !== 6"
				(click)="confirmTotp()">
			{{ isSubmitting ? 'Verifying...' : 'Verify' }}
		</button>

		<button mat-raised-button color="primary"
				*ngIf="currentStep === 'email-send'"
				[disabled]="isSubmitting || !emailAddress"
				(click)="sendEmailOtp()">
			{{ isSubmitting ? 'Sending...' : 'Send Code' }}
		</button>

		<button mat-raised-button color="primary"
				*ngIf="currentStep === 'email-verify'"
				[disabled]="isSubmitting || !emailCode || emailCode.length !== 6"
				(click)="confirmEmail()">
			{{ isSubmitting ? 'Verifying...' : 'Verify' }}
		</button>

		<button mat-raised-button color="primary"
				*ngIf="currentStep === 'complete'"
				(click)="completeEnrollment()">
			Continue
		</button>

		<button mat-raised-button
				color="primary"
				*ngIf="currentStep === 'mfa-disabled'"
				(click)="closeMfaDisabled()">
			Close
		</button>

		<button mat-raised-button
				color="primary"
				*ngIf="currentStep === 'error'"
				(click)="dialogRef.close({ error: true })">
			Close
		</button>
	</mat-dialog-actions>
</div>
